import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { InputSelect } from "components/Form/InputSelect";
import { Field, FieldProps, Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllCatalogStocks, releaseVoucher } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  voucherId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  phoneNumber: "",
  voucherCode: "",
};

const VoucherReleaseModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  voucherId,
}) => {
  const dispatch = useDispatch();

  const [voucherCode, setVoucherCode] = useState<ValueSchema | null>(null);

  const { catalogStocks } = useSelector(
    (state: any) => state.catalogStockReducer
  );
  const { isFormLoading } = useSelector((state: any) => state.voucherReducer);

  const handleCloseModal = () => {
    onClose && onClose();
    setVoucherCode(null);
  };

  const handleSubmit = (values: any) => {
    const { phoneNumber, voucherCode } = values;

    dispatch(
      releaseVoucher({ phoneNumber, voucherCode }, handleCloseModal) as any
    );
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllCatalogStocks(voucherId) as any);
    }
  }, [isOpen, dispatch, voucherId]);

  const validationSchema = yup.object({
    phoneNumber: yup
      .number()
      .typeError("That doesn't look like a phone number")
      .positive("A phone number can't start with a minus")
      .integer("A phone number can't include a decimal point")
      .required("Phone Number is required field"),
    voucherCode: yup.string().required("Voucher Code is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>Release Voucher</Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="phoneNumber">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="phoneNumber">
                          Phone Number
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="tel"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="voucherCode">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="voucherCode">
                          Voucher Code
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            selectedItem={voucherCode}
                            width={"100%"}
                            placeholder="Select Voucher Code"
                            handleSelectedItemChange={({ selectedItem }) => {
                              if (selectedItem) {
                                setVoucherCode(selectedItem);
                                setFieldValue(
                                  "voucherCode",
                                  selectedItem.value
                                );
                              }
                            }}
                            itemToString={(item) =>
                              item ? `${item.label}` : ""
                            }
                            itemValue={(item) => (item ? `${item.value}` : "")}
                            items={catalogStocks.map((catalogStock: any) => {
                              return {
                                label: catalogStock.unique_code,
                                value: catalogStock.unique_code,
                              };
                            })}
                            size={"lg"}
                            borderRadius={"6px"}
                            errors={!!meta.error && meta.touched}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    Release Voucher
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default VoucherReleaseModal;
